import React, { useContext, useMemo, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import dayjs from 'dayjs'
import 'react-dates/initialize'
import { modalContainerStyle } from '@/components/pages/accommodation-management/list-layout'
import { Button, ButtonType } from '@/components/atoms/button'
import { DateRangeFixedCalender } from '@/components/molecules/date-range-fixed-calender'
import * as api from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useForm, useWatch } from 'react-hook-form'
import { LoaderContextCreator } from '@/contexts/loader'
import { ReservationType } from '@/models/reservation'
import { useAssignConfirmDialog } from '@/components/organisms/reservation/use-assign-confirm-dialog'

/**
 * 予約複製モーダルのプロパティ
 */
type ContainerProps = {
  /** モーダルの開閉状態を制御する関数 */
  setIsModalOpen: (isModalOpen: boolean) => void
  /** 複製元の予約情報 */
  reservation: ReservationType
}

/**
 * 予約複製フォームの型定義
 */
export type FormType = {
  /** チェックイン日（YYYY-MM-DD形式） */
  checkinDate: string
}

/**
 * 予約を新しい日付で複製するモーダルコンポーネント
 *
 * 既存の予約情報を保持したまま、チェックイン日のみを変更して新しい予約を作成する。
 * チェックアウト日は元の予約の泊数から自動計算される。
 * 在庫不足の場合でもユーザーの確認を経て複製を実行できる。
 */
export const ReservationCopyModal: React.FC<ContainerProps> = ({ setIsModalOpen, reservation }) => {
  const history = useHistory()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { t } = useTranslation()
  const { errorHandler, isOutOfStockError } = useErrorHandler()
  const { AssignConfirmDialog, openConfirmDialog } = useAssignConfirmDialog()
  const { control, handleSubmit, setValue } = useForm<FormType>({
    defaultValues: {
      checkinDate: dayjs().format('YYYY-MM-DD'),
    },
  })
  const watchCheckinDate = useWatch({ control, name: 'checkinDate' })
  /** カレンダーで選択可能な最小日付（締め処理完了後の日付） */
  const [minimumDate, setMinimumDate] = useState<string>('')

  /**
   * チェックイン日と元の予約の泊数からチェックアウト日を計算
   * チェックイン日が未設定または泊数が不明な場合は空文字を返す
   * 日帰りの場合（nightsが0）でもチェックアウト日はチェックイン日と同じ日として計算される
   */
  const calculatedCheckoutDate = useMemo(() => {
    if (!watchCheckinDate || reservation.nights == null) {
      return ''
    }
    const checkin = dayjs(watchCheckinDate)
    const checkout = checkin.add(reservation.nights, 'd')
    return checkout.format('YYYY-MM-DD')
  }, [watchCheckinDate, reservation.nights])

  /**
   * 締め処理が完了した最新の日付を取得し、その翌日をカレンダーの最小選択日として設定
   * 締め処理完了日が見つからない場合は、前月の1日をデフォルト値として設定
   */
  useEffect(() => {
    const calculateMinimumDate = async () => {
      const today = dayjs()
      try {
        const response = await api.getLatestConfirmedDate()

        if (response.latestConfirmedDate) {
          const latestConfirmedDate = dayjs(response.latestConfirmedDate)
          setMinimumDate(latestConfirmedDate.add(1, 'day').format('YYYY-MM-DD'))
        } else {
          setMinimumDate(today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'))
        }
      } catch (e) {
        setMinimumDate(today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'))
      }
    }

    calculateMinimumDate()
  }, [])

  /**
   * 予約複製リクエストの事前バリデーション
   * チェックアウト日の計算が完了していることを確認する
   */
  const validateCopyRequest = (): void => {
    if (!calculatedCheckoutDate) {
      throw new Error('チェックアウト日の計算に失敗しました')
    }
  }

  /**
   * 在庫チェックを実行し、在庫不足の場合は確認ダイアログを表示
   * ユーザーが確認した場合のみ複製を続行する
   */
  const checkStockWithConfirmation = async (checkinDate: string, checkoutDate: string): Promise<boolean> => {
    try {
      await api.validateReservationStock({
        reservationId: reservation.reservationId,
        checkinDate,
        checkoutDate,
      })
      return true
    } catch (e) {
      if (!isOutOfStockError(e as Error)) {
        throw e
      }
      setIsLoading(false)
      if (!(await openConfirmDialog())) {
        return false
      }
      setIsLoading(true)
      return true
    }
  }

  /**
   * 複製された予約の詳細ページに遷移
   * モーダルを閉じた後、新しい予約IDをURLパラメータに含めて遷移し、ページをリロードする
   * リロードにより、予約詳細ページで最新のデータが取得され、リストにも正しく表示される
   * チェックイン時の処理と同様に、リロード前にローディングを解除する
   */
  const navigateToNewReservation = (reservationId: string): void => {
    setIsModalOpen(false)
    setIsLoading(false)
    history.push({
      pathname: `/reservation/${reservationId}`,
      search: `?tabType=reservation&reservationId=${reservationId}`,
    })
    // 遷移後にリロードして、予約詳細ページで最新データを取得し、リストにも反映させる
    window.location.reload()
  }

  /**
   * 予約複製処理のメイン関数
   * バリデーション → 在庫チェック → API呼び出し → 遷移の順で実行
   * エラーはerrorHandlerで統一処理し、ローディング状態も適切に管理する
   */
  const copyReservation = async (formValue: FormType) => {
    try {
      validateCopyRequest()
      setIsLoading(true)

      const shouldContinue = await checkStockWithConfirmation(formValue.checkinDate, calculatedCheckoutDate)
      if (!shouldContinue) {
        return
      }

      const res = await api.copyReservation({
        reservationId: reservation.reservationId,
        checkinDate: formValue.checkinDate,
        checkoutDate: calculatedCheckoutDate,
      })

      navigateToNewReservation(res.reservationId)
    } catch (error) {
      errorHandler(error)
      setIsLoading(false)
    }
  }

  /**
   * フォームの有効性を判定
   * チェックイン日とチェックアウト日が両方計算できている場合のみ複製ボタンを有効化
   */
  const isValid = useMemo(() => {
    return !!watchCheckinDate && !!calculatedCheckoutDate
  }, [watchCheckinDate, calculatedCheckoutDate])

  /**
   * 日付範囲変更時のハンドラー
   * チェックアウト日は元の予約の泊数から自動計算されるため、beginDateのみを使用
   */
  const changeRangeDate = (beginDate: string, endDate: string) => {
    if (beginDate) {
      setValue('checkinDate', beginDate)
    }
  }

  return (
    <div css={modalContainerStyle}>
      <div css={modalBodyContainerStyle}>
        <h3 css={modalHeaderStyle}>{t('Copy reservation')}</h3>
        <div css={modalMainContainerStyle}>
          <div>
            <div css={fieldTittleStyle}>
              {t('Check-in date')}
              <div>※</div>
            </div>
            <DateRangeFixedCalender
              beginDate={watchCheckinDate}
              endDate={undefined}
              changeRangeDate={changeRangeDate}
              minimumDate={minimumDate}
              disableEndDate={true}
            />
          </div>
        </div>
        <div css={modalButtonSectionStyle}>
          <Button
            width={110}
            height={38}
            buttonType={3}
            marginRight={16}
            onClick={() => {
              setIsModalOpen(false)
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            width={110}
            height={40}
            buttonType={isValid ? ButtonType.Primary : ButtonType.PrimaryDisabled}
            isDisabled={!isValid}
            onClick={handleSubmit(copyReservation)}
          >
            {t('Create')}
          </Button>
        </div>
      </div>
      <AssignConfirmDialog />
    </div>
  )
}

// モーダル
const modalBodyContainerStyle = css({
  position: 'relative',
  top: '42%',
  left: '50%',
  width: 570,
  zIndex: 102,
  opacity: 1,
  background: '#FFF',
  borderRadius: 5,
  transform: 'translate(-50%, -50%)',
})

const modalHeaderStyle = css({
  height: 40,
  margin: '22px 32px',
  fontWeight: 'bold',
  fontSize: 18,
  color: '#272727',
  paddingTop: 22,
})

const modalMainContainerStyle = css({
  padding: 32,
  backgroundColor: '#F2F2F2',
  display: 'flex',
  '> div': { margin: 'auto' },
})

const fieldTittleStyle = css({
  fontSize: 14,
  fontWeight: 'bold',
  color: '#676767',
  marginBottom: 12,
  display: 'flex',
  '> div': {
    fontSize: 8,
    paddingLeft: 4.75,
  },
})

const modalButtonSectionStyle = css({
  display: 'flex',
  padding: '11px 32px 11px 32px',
  justifyContent: 'flex-end',
  boxShadow: '0px 0px 6px #0000001A',
})
